import { suggestEntityColor, THEME_PRESETS, type Label } from "@app/core";
import {
  Bell,
  Button,
  Calendar,
  Card,
  CheckCircle2,
  Clock,
  Input,
  Palette,
  Tag,
  Upload,
  cn,
} from "@app/ui";
import { useEffect, useState, type ReactNode } from "react";
import { DemoPlayer } from "../demos/DemoPlayer.js";
import { TopicsPanel } from "../organize/index.js";
import { fireTestNotification } from "../reminders/index.js";
import { TimeZoneField } from "../task-form/TimeZoneField.js";
import { SUPPORTED_LANGUAGES, type I18nKey } from "../../lib/language.js";
import { useI18n } from "../../lib/useI18n.js";
import { detectPlatform } from "../../platform.js";
import { useAuthStore } from "../../stores/auth.store.js";
import { useGoogleAuthStore } from "../../stores/googleAuth.store.js";
import { useIcsStore } from "../../stores/ics.store.js";
import { useLabelsStore } from "../../stores/labels.store.js";
import { preloadPaletteBoardForPreset } from "../../palettePicker/index.js";
import { useSettingsStore } from "../../stores/settings.store.js";
import { useThemeStore } from "../../stores/theme.store.js";
import { useTopicsStore } from "../../stores/topics.store.js";
import { toast } from "../../stores/toast.store.js";

type Step = "account" | "notifications" | "import" | "theme" | "organization" | "time";
const ONBOARDING_DONE_STEP = "done";
const WEB_APP_URL = "https://yourcalendar.app";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.yourcalendar.app";
const PLAY_STORE_BADGES = {
  de: "https://play.google.com/intl/en_us/badges/static/images/badges/de_badge_web_generic.png",
  en: "https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png",
};

const STEPS: Array<{ id: Step; titleKey: I18nKey; Icon: typeof Bell }> = [
  { id: "account", titleKey: "onboarding.stepAccount", Icon: CheckCircle2 },
  { id: "notifications", titleKey: "onboarding.stepNotifications", Icon: Bell },
  { id: "import", titleKey: "onboarding.stepImport", Icon: Upload },
  { id: "theme", titleKey: "onboarding.stepTheme", Icon: Palette },
  { id: "organization", titleKey: "onboarding.stepOrganization", Icon: Tag },
  { id: "time", titleKey: "onboarding.stepTime", Icon: Clock },
];

/**
 * First-run setup. The order is intentionally opinionated:
 * account choice, notifications, import/sync, theme, organization, locale/time.
 */
export function OnboardingScreen() {
  const { t } = useI18n();
  const loaded = useSettingsStore((s) => s.loaded);
  const onboardingComplete = useSettingsStore((s) => s.onboardingComplete);
  const onboardingStep = useSettingsStore((s) => s.onboardingStep);
  const setOnboardingComplete = useSettingsStore((s) => s.setOnboardingComplete);
  const setOnboardingStep = useSettingsStore((s) => s.setOnboardingStep);
  const [stepIndex, setStepIndex] = useState(() => stepIndexFrom(onboardingStep));

  const authLoaded = useAuthStore((s) => s.loaded);
  const loadAuth = useAuthStore((s) => s.load);

  useEffect(() => {
    if (!authLoaded) void loadAuth();
  }, [authLoaded, loadAuth]);
  useEffect(() => {
    setStepIndex(stepIndexFrom(onboardingStep));
  }, [onboardingStep]);

  if (!loaded || !authLoaded || onboardingComplete) return null;

  const step = STEPS[stepIndex]!;
  const first = stepIndex === 0;
  const last = stepIndex === STEPS.length - 1;
  const goToStep = (index: number) => {
    const next = Math.max(0, Math.min(STEPS.length - 1, index));
    setStepIndex(next);
    void setOnboardingStep(STEPS[next]!.id);
  };
  const finish = () => {
    void Promise.all([setOnboardingStep(ONBOARDING_DONE_STEP), setOnboardingComplete(true)]);
  };
  const goNext = () => {
    if (last) finish();
    else goToStep(stepIndex + 1);
  };

  // z-[44]: above the app chrome (mobile nav/drawers are z-40) but BELOW the
  // mobile sheet (45) / dialogs (50) / toasts (60) — the ICS-import target
  // dialog and the notification-test toasts open FROM this screen and must
  // paint above it, not invisibly behind.
  return (
    <div className="fixed inset-0 z-[44] flex items-center justify-center bg-background/85 p-4 backdrop-blur-sm">
      <Card className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden p-0 shadow-2xl">
        <div className="border-b px-5 py-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold">{t("onboarding.title")}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.intro")}</p>
            </div>
            <button
              type="button"
              className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={finish}
            >
              {t("onboarding.skip")}
            </button>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {STEPS.map(({ id, titleKey, Icon }, index) => {
              const active = index === stepIndex;
              const done = index < stepIndex;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => goToStep(index)}
                  className={cn(
                    "flex min-w-0 items-center gap-2 rounded-md border px-2 py-2 text-left text-xs transition-colors",
                    active ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent",
                  )}
                >
                  <Icon className={cn("h-4 w-4 shrink-0", done && "text-primary")} />
                  <span className="truncate">{t(titleKey)}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 overflow-auto px-5 py-5">
          <div className="mb-5 flex items-center gap-2">
            <step.Icon className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">{t(step.titleKey)}</h2>
            <span className="ml-auto text-xs text-muted-foreground">
              {stepIndex + 1} / {STEPS.length}
            </span>
          </div>

          {step.id === "account" && <AccountStep onDone={goNext} />}
          {step.id === "notifications" && <NotificationsStep />}
          {step.id === "import" && <ImportSyncStep />}
          {step.id === "theme" && <ThemeStep />}
          {step.id === "organization" && <OrganizationStep />}
          {step.id === "time" && <LocaleTimeStep />}
        </div>

        <div className="flex items-center justify-between gap-3 border-t px-5 py-4">
          <Button
            type="button"
            variant="outline"
            disabled={first}
            onClick={() => goToStep(stepIndex - 1)}
          >
            {t("common.back")}
          </Button>
          <Button type="button" onClick={goNext}>
            {last ? t("common.done") : t("common.next")}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function stepIndexFrom(step: string | null): number {
  const index = STEPS.findIndex((item) => item.id === step);
  return index >= 0 ? index : 0;
}

function AccountStep({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const signedIn = Boolean(useAuthStore((s) => s.accessToken));
  const user = useAuthStore((s) => s.user);
  const busy = useAuthStore((s) => s.busy);
  const error = useAuthStore((s) => s.error);
  const signup = useAuthStore((s) => s.signup);
  const login = useAuthStore((s) => s.login);
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);
  const googleAvailable = useAuthStore((s) => s.googleAvailable);
  const [mode, setMode] = useState<"choose" | "signup" | "login">("choose");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function submitAccount() {
    const ok = mode === "signup" ? await signup(email, password) : await login(email, password);
    if (ok) {
      setPassword("");
      onDone();
    }
  }

  async function onGoogle() {
    if (await loginWithGoogle()) onDone();
  }

  if (signedIn) {
    return (
      <div className="max-w-xl rounded-lg border bg-card p-4">
        <p className="text-sm text-muted-foreground">
          {t("sync.signedInAs", { email: "" })}
          <span className="font-medium text-foreground">{user?.email}</span>
        </p>
        <Button className="mt-4" onClick={onDone}>{t("common.next")}</Button>
      </div>
    );
  }

  if (mode === "choose") {
    return (
      <div className="grid gap-4 md:grid-cols-[1fr_0.8fr]">
        <div className="rounded-lg border bg-card p-4">
          <h3 className="font-medium">{t("common.account")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.accountNote")}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => setMode("signup")}>
              {t("onboarding.createAccount")}
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => setMode("login")}>
              {t("sync.signIn")}
            </Button>
            {googleAvailable() ? (
              <Button variant="outline" disabled={busy} onClick={() => void onGoogle()}>
                {busy ? "..." : t("onboarding.continueWithGoogle")}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="rounded-lg border bg-muted/30 p-4">
          <h3 className="font-medium">{t("onboarding.continueWithout")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.withoutNote")}</p>
          <Button className="mt-4" variant="outline" disabled={busy} onClick={onDone}>
            {t("common.next")}
          </Button>
        </div>

        <CrossPlatformCard />
      </div>
    );
  }

  return (
    <form
      className="max-w-sm rounded-lg border bg-card p-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submitAccount();
      }}
    >
      <h3 className="font-medium">{mode === "signup" ? t("sync.createAccount") : t("sync.signIn")}</h3>
      <div className="mt-4 flex flex-col gap-2">
        <Input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />
        <Input
          type="password"
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={mode === "signup" ? t("sync.passwordSignup") : t("sync.password")}
        />
      </div>
      {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
      <div className="mt-4 flex items-center justify-between gap-2">
        <Button type="submit" disabled={busy || !email || !password}>
          {busy ? "..." : mode === "signup" ? t("sync.createAccount") : t("sync.signIn")}
        </Button>
        <button
          type="button"
          className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => setMode("choose")}
        >
          {t("common.back")}
        </button>
      </div>
    </form>
  );
}

function CrossPlatformCard() {
  const { t } = useI18n();
  const language = useSettingsStore((s) => s.language);
  const platform = detectPlatform();
  const inAndroidApp = platform === "capacitor";
  const href = inAndroidApp ? WEB_APP_URL : PLAY_STORE_URL;
  const playStoreBadge = language === "de" ? PLAY_STORE_BADGES.de : PLAY_STORE_BADGES.en;

  return (
    <section className="rounded-lg border bg-card p-4 md:col-span-2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-medium">
            {inAndroidApp ? t("onboarding.webAvailableTitle") : t("onboarding.appAvailableTitle")}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {inAndroidApp ? t("onboarding.webAvailableBody") : t("onboarding.appAvailableBody")}
          </p>
        </div>
        <a
          className={cn(
            "inline-flex shrink-0 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            inAndroidApp
              ? "h-10 border border-input bg-background px-4 text-sm font-medium hover:bg-accent"
              : "hover:opacity-90",
          )}
          href={href}
          target="_blank"
          rel="noreferrer noopener"
        >
          {inAndroidApp ? (
            t("onboarding.openWebApp")
          ) : (
            <img
              src={playStoreBadge}
              alt={t("onboarding.openPlayStore")}
              className="h-16 w-auto sm:h-20"
            />
          )}
        </a>
      </div>
    </section>
  );
}

function NotificationsStep() {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [tested, setTested] = useState(false);

  async function testNow() {
    setBusy(true);
    try {
      const { background } = await fireTestNotification();
      setTested(true);
      toast.success(background ? t("onboarding.notifScheduled") : t("onboarding.notifSent"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e), 8000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-xl rounded-lg border bg-card p-4">
      <h3 className="font-medium">{t("onboarding.notifTitle")}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.notifBody")}</p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button disabled={busy} onClick={() => void testNow()}>
          <Bell className="h-4 w-4" />
          {busy ? t("onboarding.notifTesting") : t("onboarding.notifTest")}
        </Button>
        {tested ? <span className="text-sm text-muted-foreground">{t("onboarding.notifTested")}</span> : null}
      </div>
    </div>
  );
}

function ImportSyncStep() {
  const { t } = useI18n();
  const icsBusy = useIcsStore((s) => s.busy);
  const importIcs = useIcsStore((s) => s.importIcs);
  const googleLoaded = useGoogleAuthStore((s) => s.loaded);
  const loadGoogle = useGoogleAuthStore((s) => s.load);
  const googleConnected = useGoogleAuthStore((s) => s.connected);
  const googleBusy = useGoogleAuthStore((s) => s.busy);
  const connectGoogle = useGoogleAuthStore((s) => s.connect);

  useEffect(() => {
    if (!googleLoaded) void loadGoogle();
  }, [googleLoaded, loadGoogle]);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <ActionPanel
        icon={<Calendar className="h-4 w-4" />}
        title={t("onboarding.importIcsTitle")}
        body={t("onboarding.importIcsBody")}
        action={
          <Button variant="outline" disabled={icsBusy} onClick={() => void importIcs()}>
            {icsBusy ? t("onboarding.importing") : t("data.importIcs")}
          </Button>
        }
      />
      <ActionPanel
        icon={<Calendar className="h-4 w-4" />}
        title={t("sync.googleCalendar")}
        body={t("onboarding.googleBody")}
        action={
          <Button variant="outline" disabled={googleBusy || googleConnected} onClick={() => void connectGoogle()}>
            {googleConnected ? t("onboarding.connected") : googleBusy ? t("sync.connecting") : t("sync.connectGoogle")}
          </Button>
        }
      />
    </div>
  );
}

function ThemeStep() {
  const { t } = useI18n();
  const activePreset = useThemeStore((s) => s.config.preset);
  const setPreset = useThemeStore((s) => s.setPreset);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {THEME_PRESETS.map((preset) => {
          const active = preset.id === activePreset;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => setPreset(preset.id)}
              onFocus={() => preloadPaletteBoardForPreset(preset.id)}
              onPointerEnter={() => preloadPaletteBoardForPreset(preset.id)}
              className={cn(
                "flex flex-col gap-2 rounded-lg border p-3 text-left transition-colors",
                active ? "border-primary ring-2 ring-primary" : "border-border hover:bg-accent",
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{preset.name}</span>
                <span className="rounded-full border border-border px-2 py-0.5 text-[calc(10px*var(--app-text-scale))] uppercase text-muted-foreground">
                  {preset.defaultMode}
                </span>
              </span>
              <span className="flex h-5 w-full overflow-hidden rounded">
                {preset.entityPalette.slice(0, 10).map((hex, i) => (
                  <span key={i} className="h-full flex-1" style={{ background: hex }} />
                ))}
              </span>
              <span className="text-xs text-muted-foreground">{preset.description}</span>
            </button>
          );
        })}
      </div>

      <section className="rounded-lg border bg-card p-4">
        <h3 className="font-medium">{t("onboarding.themePreviewTitle")}</h3>
        <p className="mb-3 mt-1 text-sm text-muted-foreground">{t("onboarding.themePreviewBody")}</p>
        <DemoPlayer id="theme-preview" loop={false} footer={false} />
      </section>
    </div>
  );
}

function OrganizationStep() {
  const { t } = useI18n();
  const labels = useLabelsStore((s) => s.labels);
  const hydrateTopics = useTopicsStore((s) => s.hydrate);
  const hydrateLabels = useLabelsStore((s) => s.hydrate);
  const themeConfig = useThemeStore((s) => s.config);
  const addLabel = useLabelsStore((s) => s.add);
  const renameLabel = useLabelsStore((s) => s.rename);
  const [newLabel, setNewLabel] = useState("");

  useEffect(() => { void hydrateTopics(); void hydrateLabels(); }, [hydrateTopics, hydrateLabels]);

  async function createLabel() {
    const name = newLabel.trim();
    if (!name) return;
    // Auto-assign a distinct palette color (TopicsPanel does the same for
    // topics); labels are recolored later via the palette board in Settings →
    // Topics, Labels and Calendars.
    const usedColors = labels.map((label) => label.color).filter((color): color is string => !!color);
    await addLabel(name, suggestEntityColor(themeConfig, usedColors).hex);
    setNewLabel("");
  }

  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-lg border bg-card p-4">
        <h3 className="font-medium">{t("common.topics")}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.topicsBody")}</p>
        {/* The REAL topic editor from Settings: drag rows to reorder, drag
            right/left to nest/promote, click the emoji slot for the picker. */}
        <div className="mt-3">
          <TopicsPanel />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h3 className="font-medium">{t("common.labels")}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.labelsBody")}</p>
        <div className="mt-4 flex flex-col gap-2">
          {labels.map((label) => (
            <LabelRow key={label.id} label={label} onRename={renameLabel} />
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void createLabel(); }}
            placeholder={t("lists.newLabel")}
          />
          <Button type="button" onClick={() => void createLabel()}>{t("common.add")}</Button>
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h3 className="font-medium">{t("demos.nestDemoTitle")}</h3>
        <p className="mb-3 mt-1 text-sm text-muted-foreground">{t("demos.nestDemoBody")}</p>
        <DemoPlayer id="basics-nest" />
      </section>
    </div>
  );
}

function LabelRow({
  label,
  onRename,
}: {
  label: Label;
  onRename: (id: string, name: string) => Promise<void>;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="h-9 w-9 shrink-0 rounded border"
        style={{ background: label.color ?? "#888888" }}
        aria-hidden
      />
      <Input value={label.name} onChange={(e) => void onRename(label.id, e.target.value)} />
    </div>
  );
}

function LocaleTimeStep() {
  const { t } = useI18n();
  const language = useSettingsStore((s) => s.language);
  const setLanguage = useSettingsStore((s) => s.setLanguage);
  const timeFormat = useSettingsStore((s) => s.timeFormat);
  const setTimeFormat = useSettingsStore((s) => s.setTimeFormat);
  const weekStartsOn = useSettingsStore((s) => s.weekStartsOn);
  const setWeekStartsOn = useSettingsStore((s) => s.setWeekStartsOn);
  const defaultTz = useSettingsStore((s) => s.defaultTz);
  const setDefaultTz = useSettingsStore((s) => s.setDefaultTz);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <section className="rounded-lg border bg-card p-4">
        <h3 className="font-medium">{t("common.language")}</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {SUPPORTED_LANGUAGES.map((item) => (
            <Button
              key={item.value}
              variant={language === item.value ? "primary" : "outline"}
              onClick={() => void setLanguage(item.value)}
              title={item.label}
            >
              {item.nativeName}
            </Button>
          ))}
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h3 className="font-medium">{t("general.timeFormat")}</h3>
        <div className="mt-3 flex gap-2">
          <Button variant={timeFormat === "24h" ? "primary" : "outline"} onClick={() => void setTimeFormat("24h")}>{t("general.timeFormat.24")}</Button>
          <Button variant={timeFormat === "12h" ? "primary" : "outline"} onClick={() => void setTimeFormat("12h")}>{t("general.timeFormat.12")}</Button>
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h3 className="font-medium">{t("defaults.weekStart")}</h3>
        <div className="mt-3 flex gap-2">
          <Button variant={weekStartsOn === 0 ? "primary" : "outline"} onClick={() => void setWeekStartsOn(0)}>{t("defaults.sunday")}</Button>
          <Button variant={weekStartsOn === 1 ? "primary" : "outline"} onClick={() => void setWeekStartsOn(1)}>{t("defaults.monday")}</Button>
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <TimeZoneField value={defaultTz} onChange={(tz) => void setDefaultTz(tz)} />
      </section>
    </div>
  );
}

function ActionPanel({
  icon,
  title,
  body,
  action,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  action: ReactNode;
}) {
  return (
    <section className="flex flex-col rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 font-medium">
        {icon}
        <h3>{title}</h3>
      </div>
      <p className="mt-2 flex-1 text-sm text-muted-foreground">{body}</p>
      <div className="mt-4">{action}</div>
    </section>
  );
}

